import React, { useState } from 'react';
import { Sparkles, ChevronDown, ChevronUp, Lightbulb } from 'lucide-react';

/**
 * Read-only card displaying a previously-saved Post Event Insights snapshot.
 *
 * Renders nothing if the event has no snapshot (per spec).
 * Renders a compact KPI grid + top tier / top payment lines, with an
 * inline expand revealing the frozen recommendation list.
 */

function formatSavedAt(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
}

function formatCurrency(n) {
  return Number(n || 0).toLocaleString(undefined, {
    style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0,
  });
}

function capitalize(s) {
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function Stat({ label, value, testId, accent }) {
  return (
    <div data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-[#777] font-semibold">{label}</div>
      <div className="text-base font-semibold tabular-nums mt-0.5" style={{ color: accent || '#EAE7E1' }}>
        {value}
      </div>
    </div>
  );
}

export default function InsightsSnapshotCard({ snapshot }) {
  const [expanded, setExpanded] = useState(false);

  if (!snapshot) return null;

  const kpis = snapshot.kpis || {};
  const topTier = snapshot.top_tier || null;
  const topPayment = snapshot.top_payment_method || null;
  const recs = Array.isArray(snapshot.recommendations) ? snapshot.recommendations : [];

  return (
    <div
      className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-sm p-5"
      data-testid="manage-event-insights-snapshot"
    >
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#D4AF37]" />
            <h3 className="text-sm font-semibold text-[#EAE7E1] tracking-wide uppercase">Event Insights Snapshot</h3>
          </div>
          <p className="text-[11px] text-[#777] mt-0.5" data-testid="snapshot-saved-at">
            Saved {formatSavedAt(snapshot.created_at)}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
        <Stat label="Passes"    value={kpis.total_passes || 0}                                testId="snapshot-passes" />
        <Stat label="Attendees" value={kpis.total_attendees || 0}                              testId="snapshot-attendees" />
        <Stat label="Check-In"  value={`${Math.round(kpis.checked_in_percent || 0)}%`}         accent="#C6A66B" testId="snapshot-checkin" />
        <Stat label="Gross"     value={formatCurrency(kpis.reported_gross_total || 0)}          accent="#C6A66B" testId="snapshot-gross" />
        <Stat label="Comp"      value={`${Math.round(kpis.comp_percent || 0)}%`}                testId="snapshot-comp" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pb-4 border-b border-[#1A1A1A]">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#777] font-semibold">Top Tier</div>
          <div className="text-sm text-[#EAE7E1] mt-0.5" data-testid="snapshot-top-tier">
            {topTier ? <>{topTier.name} <span className="text-[#D4AF37] ml-1">({Math.round(topTier.percent)}%)</span></> : <span className="text-[#555]">—</span>}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#777] font-semibold">Top Payment</div>
          <div className="text-sm text-[#EAE7E1] mt-0.5" data-testid="snapshot-top-payment">
            {topPayment ? <>{capitalize(topPayment.method)} <span className="text-[#D4AF37] ml-1">({Math.round(topPayment.percent)}%)</span></> : <span className="text-[#555]">—</span>}
          </div>
        </div>
      </div>

      <div className="mt-3">
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className="inline-flex items-center gap-1.5 text-xs text-[#D4AF37] hover:text-[#F2D27A] border border-[#D4AF37]/40 rounded-sm px-3 py-1.5 transition-colors"
          data-testid="snapshot-view-button"
        >
          {expanded ? (<><ChevronUp className="w-3 h-3" /> Hide Snapshot</>) : (<><ChevronDown className="w-3 h-3" /> View Snapshot</>)}
        </button>

        {expanded && (
          <div className="mt-3 space-y-2" data-testid="snapshot-recommendations">
            <div className="flex items-center gap-2">
              <Lightbulb className="w-3.5 h-3.5 text-[#D4AF37]" />
              <h4 className="text-[10px] uppercase tracking-wider text-[#777] font-semibold">Recommendations</h4>
            </div>
            {recs.length === 0 ? (
              <p className="text-xs text-[#555]" data-testid="snapshot-recommendations-empty">
                Nothing to flag — this event was within healthy ranges.
              </p>
            ) : (
              <ul className="space-y-1.5">
                {recs.map((r, i) => (
                  <li
                    key={i}
                    className="text-[12px] leading-relaxed text-[#D4AF37] bg-[#D4AF37]/5 border border-[#D4AF37]/15 rounded-sm px-3 py-2"
                    data-testid={`snapshot-recommendation-${i}`}
                  >{r}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
